import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  Flex,
  Label,
  Table,
  Button,
  Heading3,
  TableRow,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  Pagination,
  FilterMenu,
  PageWrapper,
  StretchPanel,
  useFilterReducer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer,
} from "@ubisend/pulse-components";

import { FeatureClientCard } from "../Components";

const Features = ({
  location: {
    state: { name, project },
  },
}) => {
  const history = useHistory();

  const pagination = usePaginationReducer({ id: "features" });
  const order = useOrderableTableReducer({ id: "features" });

  const filters = useFilterReducer({
    id: "features",
    initialFilters: { name: "" },
    options: { pagination },
  });

  const query = useQuery({
    queryKey: [
      `features/${project.project_id}`,
      { ...pagination.params, ...order.params, ...filters.form },
    ],
  });

  const handleNameChange = (e) => {
    filters.updateFilters({ name: e.target.value });
  };

  return (
    <PageWrapper
      header="Features"
      subheader={`Manage features for ${name?.name ?? "your install"}`}
      actions={
        <Flex xSpace>
          <FilterMenu
            buttonProps={{ loading: query.isLoading }}
            {...filters.props}
          >
            <Flex style={{ width: "20rem" }} col>
              <Flex col fat mb>
                <Label htmlFor="name">Account Name</Label>
                <TextInput
                  id="name"
                  placeholder="Search by account name"
                  value={filters.filters.name}
                  onChange={handleNameChange}
                />
              </Flex>
            </Flex>
          </FilterMenu>
          <Button
            icon="arrowNarrowLeft"
            onClick={() => history.goBack()}
            variant="primary"
          >
            Back
          </Button>
        </Flex>
      }
    >
      {query.isError && (
        <NoResults
          text="There was a problem connecting to the project"
          subtitle="Double check the projects credentials or beg a developer for help"
        />
      )}

      {query.isSuccess && query.data.data.length === 0 && (
        <NoResults
          text="No accounts found"
          subtitle="This project has no client accounts yet"
        />
      )}

      {query.isSuccess && query.data.data.length >= 0 && (
        <StretchPanel>
          <Table loading={query.isLoading} loadingColumns={4}>
            <TableHead>
              <OrderableTableRow
                rows={[
                  { label: "Account information", sort: "account_name" },
                  { label: "Actions", sort: null },
                ]}
                {...order.props}
              />
            </TableHead>
            {!query.isLoading && (
              <TableBody>
                <TableRow key={0}>
                  <TableCell>
                    <Heading3>Install Level Feature Setting</Heading3>
                  </TableCell>
                  <TableCell>
                    <Flex xSpace right>
                      <Button
                        as={Link}
                        icon="eye"
                        to={{
                          pathname: `/features/${project.project_id}/install/logs`,
                          state: {
                            project: {
                              name: project.name,
                              id: project.project_id,
                            },
                            account: null,
                          },
                        }}
                      >
                        View Logs
                      </Button>
                      <Button
                        as={Link}
                        icon="key"
                        to={{
                          pathname: `/features/${project.project_id}/install`,
                          state: {
                            project: {
                              name: project.name,
                              id: project.project_id,
                            },
                            account: null,
                          },
                        }}
                      >
                        Edit Features
                      </Button>
                    </Flex>
                  </TableCell>
                </TableRow>
                {query.data.data.map((account) => (
                  <TableRow key={account.account_id}>
                    <TableCell>
                      <FeatureClientCard
                        client={{
                          logo: null,
                          name: account.account_name,
                          account,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Flex xSpace right>
                        <Button
                          as={Link}
                          icon="eye"
                          to={{
                            pathname: `/features/${project.project_id}/${account.account_id}/logs`,
                            state: {
                              project: {
                                name: project.name,
                                id: project.project_id,
                              },
                              account: {
                                name: account.account_name,
                                id: account.account_id,
                              },
                            },
                          }}
                        >
                          View Logs
                        </Button>
                        <Button
                          as={Link}
                          icon="key"
                          to={{
                            pathname: `/features/${project.project_id}/${account.account_id}`,
                            state: {
                              project: {
                                name: project.name,
                                id: project.project_id,
                              },
                              account: {
                                name: account.account_name,
                                id: account.account_id,
                              },
                            },
                          }}
                        >
                          Edit Features
                        </Button>
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {query.showPagination && (
            <Pagination pagination={query.data.meta} {...pagination.props} />
          )}
        </StretchPanel>
      )}
    </PageWrapper>
  );
};

export default Features;
